import { Injectable, signal } from "@angular/core";

export type ColorMode = "light" | "dark";

const STORAGE_KEY = "rad-theme";

@Injectable({ providedIn: "root" })
export class ThemeService {
  /** Current UI color mode (mirrored on `document.documentElement` as `data-theme`). */
  readonly colorMode = signal<ColorMode>("light");

  constructor() {
    this.load();
  }

  /** `APP_INITIALIZER`: keep signal in sync after bootstrap (e.g. if `index.html` preset `data-theme`). */
  initFromStorage(): void {
    this.load();
  }

  toggle(): void {
    this.persist(this.colorMode() === "light" ? "dark" : "light");
  }

  setColorMode(mode: ColorMode): void {
    this.persist(mode);
  }

  private load(): void {
    if (typeof document === "undefined") return;
    const mode = this.readStoredOrDom();
    this.colorMode.set(mode);
    document.documentElement.setAttribute("data-theme", mode);
  }

  private persist(mode: ColorMode): void {
    this.colorMode.set(mode);
    if (typeof document !== "undefined") {
      document.documentElement.setAttribute("data-theme", mode);
    }
    try {
      localStorage.setItem(STORAGE_KEY, mode);
    } catch {
      /* private mode / quota */
    }
  }

  private readStoredOrDom(): ColorMode {
    try {
      const stored = localStorage.getItem(STORAGE_KEY);
      if (stored === "dark" || stored === "light") return stored;
    } catch {
      /* ignore */
    }
    if (typeof document !== "undefined") {
      const attr = document.documentElement.getAttribute("data-theme");
      if (attr === "dark" || attr === "light") return attr;
    }
    return "light";
  }
}
